import "../sections/sections.css";
import "./contact.css";

import React from "react";
import { Link } from "react-router-dom";

import { CONTACT_DETAILS, ROUTES } from "../../data/content";
import { postRequest } from "../../utils/apiHandlers";
import { BrandIcon, LineIcon } from "../general/Icons";
import PageMeta from "../general/PageMeta";
import PageHero from "../sections/PageHero";
import SpecialNotice from "../sections/SpecialNotice";

const HERO = {
  image: { src: "/images/heroes/contact.webp", width: 1920, height: 799 },
  title: "Contact Us",
  text: "Leave us your info and we will get back to you.",
};

const SITE_VISITS = {
  title: "Site Visits",
  paragraphs: [
    "We are fully open and operational, so you are welcome to pop past for a site visit. Kindly call us to make an appointment.",
    "However, we can also conduct Zoom meetings should you feel more comfortable to do so.",
  ],
};

const EMPTY = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  message: "",
  website: "", //Honeypot
};

const Field = ({ label, isRequired, className = "", children }) => (
  <label className={`contact-field ${className}`}>
    <span className={"contact-label"}>
      {label}
      {isRequired ? (
        <span
          className={"contact-required"}
          aria-hidden={"true"}
        >
          {" "}*
        </span>
      ) : null}
    </span>
    {children}
  </label>
);

const ContactForm = () => {
  const [form, setForm] = React.useState(EMPTY);
  const [isSending, setIsSending] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");
  const [sentTo, setSentTo] = React.useState(null);

  const update = (prop) => (e) => setForm({ ...form, [prop]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorText("");
    setIsSending(true);
    try {
      await postRequest("/Contact/SendContactMessage", form);
      setSentTo(form.firstName);
      setForm(EMPTY);
    }
    catch (err) {
      setErrorText(err.message);
    }
    setIsSending(false);
  };

  if (sentTo !== null) {
    return (
      <div
        className={"contact-success"}
        role={"status"}
      >
        <h3>Thank you{sentTo ? `, ${sentTo}` : ""}!</h3>
        <p>We&apos;ve received your message and will get back to you soon.</p>
        <button
          type={"button"}
          className={"btn"}
          onClick={() => setSentTo(null)}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      className={"contact-form"}
      onSubmit={onSubmit}
    >
      <Field
        label={"Enter Your Name"}
        isRequired
      >
        <input
          type={"text"}
          value={form.firstName}
          onChange={update("firstName")}
          required
          maxLength={100}
          autoComplete={"given-name"}
        />
      </Field>
      <Field label={"Enter Your Last Name"}>
        <input
          type={"text"}
          value={form.lastName}
          onChange={update("lastName")}
          maxLength={100}
          autoComplete={"family-name"}
        />
      </Field>
      <Field
        label={"Enter Your Phone"}
        isRequired
      >
        <input
          type={"tel"}
          value={form.phone}
          onChange={update("phone")}
          required
          maxLength={50}
          autoComplete={"tel"}
        />
      </Field>
      <Field
        label={"Enter Your Email"}
        isRequired
      >
        <input
          type={"email"}
          value={form.email}
          onChange={update("email")}
          required
          maxLength={200}
          autoComplete={"email"}
        />
      </Field>
      <Field
        label={"Type Your Message Here"}
        isRequired
        className={"is-wide"}
      >
        <textarea
          value={form.message}
          onChange={update("message")}
          required
          maxLength={5000}
          rows={5}
        />
      </Field>
      <label
        className={"contact-honeypot"}
        aria-hidden={"true"}
      >
        Website
        <input
          type={"text"}
          value={form.website}
          onChange={update("website")}
          tabIndex={-1}
          autoComplete={"off"}
        />
      </label>

      {errorText ? (
        <p
          className={"contact-error"}
          role={"alert"}
        >
          {errorText}
        </p>
      ) : null}

      <div className={"contact-submit"}>
        <button
          type={"submit"}
          className={"btn"}
          disabled={isSending}
        >
          {isSending ? "Sending..." : "Submit"}
        </button>
      </div>
    </form>
  );
};

//Contact details panel. Wix linked the landline to a different (011) number and one email link to another address - both fixed
const ContactDetails = () => (
  <address className={"contact-details"}>
    <div className={"contact-detail"}>
      <LineIcon name={"phone"} />
      <p>
        Landline: <a href={CONTACT_DETAILS.LANDLINE.link}>{CONTACT_DETAILS.LANDLINE.label}</a>
        <br />
        Cellphone: <a href={CONTACT_DETAILS.CELL.link}>{CONTACT_DETAILS.CELL.label}</a>
      </p>
    </div>
    <div className={"contact-detail"}>
      <span className={"contact-detail-brand"}>
        <BrandIcon name={"whatsapp"} />
      </span>
      <p>
        WhatsApp:{" "}
        <a
          href={CONTACT_DETAILS.WHATSAPP_LINK}
          target={"_blank"}
          rel={"noopener noreferrer"}
        >
          {CONTACT_DETAILS.CELL.label}
        </a>
      </p>
    </div>
    <div className={"contact-detail"}>
      <LineIcon name={"mail"} />
      <p>
        Email: <a href={`mailto:${CONTACT_DETAILS.EMAIL}`}>{CONTACT_DETAILS.EMAIL}</a>
      </p>
    </div>
    <div className={"contact-detail"}>
      <LineIcon name={"pin"} />
      <p>
        Address:{" "}
        <a
          href={CONTACT_DETAILS.MAP_LINK}
          target={"_blank"}
          rel={"noopener noreferrer"}
        >
          {CONTACT_DETAILS.ADDRESS.map((line, i) => (
            <React.Fragment key={line}>
              {i > 0 ? <br /> : null}
              {line}
            </React.Fragment>
          ))}
        </a>
      </p>
    </div>
    <div className={"contact-detail"}>
      <LineIcon name={"lock"} />
      <p>
        By contacting us, you agree to the terms of our <Link to={ROUTES.PRIVACY}>Privacy Policy</Link>.
      </p>
    </div>
  </address>
);

const Contact = () => (
  <React.Fragment>
    <PageMeta
      title={"Contact"}
      description={"Contact Estia Venue & Events - call 076 777 7777, WhatsApp 084 883 7757 or email bookings@estiaevents.co.za. Site visits by appointment."}
    />
    <PageHero {...HERO} />
    <SpecialNotice {...SITE_VISITS} />
    <section className={"contact-section"}>
      <div className={"container contact-grid"}>
        <div className={"contact-form-wrap"}>
          <h2 className={"contact-form-title"}>Please fill out this form and we will get back to you</h2>
          <ContactForm />
        </div>
        <ContactDetails />
      </div>
    </section>
  </React.Fragment>
);

export default Contact;
